import { postQuery, postForm } from './restClient';
import { API_V1 } from 'config';
import type { AnalysisResult } from 'types/analysis';

type Exchange = 'binance' | 'mexc';
export type Engine = 'brainer_v0' | 'brainer_v0_1' | 'brainer_v1' | 'brainer_v1_nohm' | 'enforcer' | 'enforcer_v3' | 'petar_v4' | 'stojan_v5' | 'claude';

/**
 * Resolve a v0 screenshot's MinIO object key to a backend stream URL. The API
 * streams the bytes (MinIO isn't browser-reachable), so an <img src> here just
 * works — no token needed (the key is an unguessable UUID).
 */
export function v0ImageUrl(objectKey: string): string {
	return `${API_V1}/claude/v0-image/${objectKey}`;
}

export type EngineOption = {
	value: Engine;
	label: string;
	badge: string;
	version: string;
	short: string;
	/** Present in the registry so its rows render with a name, but not offered as
	 *  a run target in the main engine picker. */
	pickerHidden?: boolean;
};

/**
 * The analysis engines, in display order. Single source of truth for the
 * selector dropdown, the header badge, and the human-readable labels:
 *   - `label`   — full name used in loading states / the trace chip (e.g. "Enforcer (v2)").
 *   - `badge`   — short chip text for the panel header (e.g. "Enforcer v2").
 *   - `version` — the discriminator alone (e.g. "v2"), for version-first labels.
 *   - `short`   — the name minus its version suffix, the tail of a version-first label.
 *
 * `version` + `short` exist because dense surfaces (the History table's 110px
 * Engine column) truncate the tail, and the version is exactly the part worth
 * keeping — see `engineVersionLabel`.
 *
 * `brainer_v1_nohm` is not a separate backend engine: it is v1 run with the
 * liquidation heatmap withheld (`heatmap=false`), the control half of the
 * with/without A/B. Same relationship v0 has to v0.1, in the other direction.
 * The backend audits it under this exact string, so History filters on it too.
 */
export const ENGINE_OPTIONS: EngineOption[] = [
	{ value: 'brainer_v0', label: 'BrainerPro + Claude (v0)', badge: 'Brainer v0', version: 'v0', short: 'BrainerPro + Claude' },
	{ value: 'brainer_v0_1', label: 'BrainerPro + Heatmap (v0.1)', badge: 'Brainer v0.1', version: 'v0.1', short: 'BrainerPro + Heatmap' },
	{ value: 'brainer_v1', label: 'BrainerPro Data + Heatmap (v1)', badge: 'Brainer v1', version: 'v1', short: 'BrainerPro Data + HM' },
	{ value: 'brainer_v1_nohm', label: 'BrainerPro Data, NO Heatmap (v1)', badge: 'Brainer v1 ⌀HM', version: 'v1 ⌀HM', short: 'BrainerPro Data' },
	{ value: 'enforcer', label: 'Enforcer (v2)', badge: 'Enforcer v2', version: 'v2', short: 'Enforcer' },
	{ value: 'enforcer_v3', label: 'Enforcer (v3)', badge: 'Enforcer v3', version: 'v3', short: 'Enforcer' },
	// petar_enforcer_v4.py + the blueprint-merge verbalizer. Always runnable under
	// this key; it is also what plain 'enforcer' runs once the backend's
	// ENFORCER_IMPL flag is flipped (History then splits those by enforcer_version).
	{ value: 'petar_v4', label: 'Enforcer (petar v4)', badge: 'Enforcer v4', version: 'v4', short: 'Enforcer' },
	// stojan_enforcer_v5.py — Stojan's fork of Enforcer_v3.py (its natural control) that decides
	// the trade set itself (DECISIONS_JSON); the backend only reports how exactly Claude rendered
	// it (trace step3.decisions_validation). Extra engine: listed only with `extra_engines`.
	{ value: 'stojan_v5', label: 'Enforcer (stojan v5)', badge: 'Enforcer v5', version: 'v5', short: 'Enforcer' },
	// Runnable (the backend registers it, and it is a fork target), but kept out
	// of the main picker, which has never offered it. It is listed here so the
	// audit log stops rendering the bare id "claude".
	{ value: 'claude', label: 'Claude in-context calc (v3)', badge: 'Claude v3', version: 'v3', short: 'Claude in-context', pickerHidden: true },
];

/** The engine every account may run (what mobile/web always get). */
export const DEFAULT_ENGINE: Engine = 'enforcer';

/**
 * The v0–v3 engines: listed for every account. Mirrors the backend's
 * `claude_service.OPEN_ENGINES` (plus the v1 no-heatmap variant, which the
 * backend treats as a flag on brainer_v1). Anything not listed — petar_v4,
 * stojan_v5 and every future v4+ engine — is an extra engine.
 */
const OPEN_ENGINES: ReadonlySet<Engine> = new Set<Engine>([
	'brainer_v0', 'brainer_v0_1', 'brainer_v1', 'brainer_v1_nohm', 'enforcer', 'enforcer_v3', 'claude',
]);

/**
 * Mirror of the backend's `claude_service.can_use_engine`: v0–v3 are open to
 * everyone; v4 and above need the `extra_engines` flag. No superuser/admin
 * bypass — superuser only grants the dashboard. The backend enforces it
 * regardless (403); this keeps the pickers from listing engines the user may
 * not know about.
 */
export function canUseEngine(
	user: { extra_engines?: boolean } | null | undefined,
	engine: Engine,
): boolean {
	return OPEN_ENGINES.has(engine) || !!user?.extra_engines;
}

/**
 * Resolve an engine id to its display metadata.
 *
 * Falls back to deriving the `_nohm` variant of a known engine: the backend's
 * `audit_engine_label()` appends that suffix to *any* engine run without the
 * heatmap, so the audit log can hold `enforcer_nohm`, `claude_nohm`… which are
 * never listed here. Without this, History renders those rows as a raw id.
 * `brainer_v1_nohm` is an explicit entry, so the exact match wins first and the
 * suffix is never applied twice.
 */
function findEngine(engine: string): EngineOption | undefined {
	const exact = ENGINE_OPTIONS.find((e) => e.value === engine);
	if (exact) return exact;

	const NOHM = '_nohm';
	if (engine.endsWith(NOHM)) {
		const base = ENGINE_OPTIONS.find((e) => e.value === engine.slice(0, -NOHM.length));
		if (base) {
			return {
				...base,
				value: engine as Engine,
				label: `${base.label} — NO Heatmap`,
				badge: `${base.badge} ⌀HM`,
				version: `${base.version} ⌀HM`,
			};
		}
	}
	return undefined;
}

/** Human-readable label for an engine — shared across the panel and trace chip. */
export function engineLabel(engine: Engine): string {
	return findEngine(engine)?.label ?? engine;
}

/** Short chip label for the panel header badge (e.g. "Enforcer v2"). */
export function engineBadgeLabel(engine: Engine): string {
	return findEngine(engine)?.badge ?? engine;
}

/**
 * Version-first label for dense tables: "(v1) BrainerPro Data + HM". The
 * History Engine column truncates at 110px, so leading with the version keeps
 * the one field that tells two runs apart visible without hovering.
 */
export function engineVersionLabel(engine: Engine): string {
	const o = findEngine(engine);
	return o ? `(${o.version}) ${o.short}` : engine;
}

/**
 * Deep AI analysis. Runs the full pipeline on the backend and returns the
 * structured trade analysis. `engine` selects the A/B calc path:
 *   - 'enforcer'    (v2): we pre-compute the trade logic via the Enforcer subprocess
 *   - 'enforcer_v3'     : same pipeline, but the revised Enforcer_v3.py builds the blueprint
 *   - 'claude'      (v3): Claude runs the calc engine itself in-context
 *   - 'brainer_v1'  (v1): worker data + heatmap straight to Claude — no Enforcer, no chart
 *   - 'brainer_v1_nohm' : the same v1 run with the heatmap withheld (A/B control)
 * All share identical Step 1 (data fetch) and return the same StyleJson contract,
 * so the results are diff-able.
 * With debug=true the response carries a per-step trace in result.debug.
 */
export function analyzeBrainer(asset: string, timeframe: string, exchange: Exchange, engine: Engine = 'enforcer', debug = true, signal?: AbortSignal): Promise<AnalysisResult> {
	// The no-heatmap variant is a flag on the real engine, not an engine of its own.
	const heatmap = engine !== 'brainer_v1_nohm';
	const apiEngine = heatmap ? engine : 'brainer_v1';
	return postQuery<AnalysisResult>('/claude/analyze-brainer', { asset, timeframe, exchange, engine: apiEngine, heatmap, debug }, signal);
}

/**
 * BrainerPro + Claude (v0 / v0.1) — the original/old algorithm. Uploads screenshot(s)
 * and runs image → Claude-direct vision on the backend (no Enforcer, no Python calc).
 * Multipart, so it goes through `postForm` rather than the JSON helpers. When
 * `useDbHeatmap` is set (engine v0.1) the backend also injects the cached DB
 * liquidation heatmap as context; plain v0 leaves it off.
 */
export function analyzeV0(files: File[], opts: { mode?: 'single' | 'multi_tf' | 'correlation'; exchange: Exchange; debug?: boolean; useDbHeatmap?: boolean; timeframe?: string }, signal?: AbortSignal): Promise<AnalysisResult> {
	const form = new FormData();
	files.forEach((f) => form.append('screenshots', f, f.name));
	form.append('mode', opts.mode ?? 'single');
	form.append('exchange', opts.exchange);
	form.append('use_db_heatmap', String(opts.useDbHeatmap ?? false));
	// Selects the heatmap product for v0.1 (<1h → intraday, 1h+ → swing); the
	// backend can't infer it from the image, so the panel's timeframe is sent.
	if (opts.timeframe) form.append('timeframe', opts.timeframe);
	form.append('debug', String(opts.debug ?? true));
	return postForm<AnalysisResult>('/claude/analyze-v0', form, signal);
}
